import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Camera, CheckCircle2, ChevronLeft, ChevronRight, Grid3x3, RefreshCw, Search, Square, X } from 'lucide-react';
import baseapi, { API_BASE, useApiOutage } from '../baseapi';
import { LiveFrame } from '../components/junctions/LiveFrame';
import { SavedFrame } from '../components/junctions/SavedFrame';
import { ZoneEditor } from '../components/junctions/ZoneEditor';
import { readLast, saveLast } from '../utils/lastAnswer';

/**
 * Holding areas: which camera looks at which box.
 *
 * The box numbers were first given to the cameras by guesswork. This page shows the picture of every
 * holding camera next to a box selector, so somebody who knows the site can look at each picture and
 * put the camera on the right box. A change is saved at once (POST /api/holding/mapping) and the
 * Holding Area screen, the Overview and the dashboard follow without a restart.
 */
interface MapCamera {
  id: string; name: string | null; ip: string | null; state: string; people_in_view: number | null;
  area: string | null; box: string | null; role: 'box' | 'exit' | null;
  snapshot_url: string; last_snapshot_url: string;
}
interface MapBox { id: string; number: string; name: string; capacity: number; cameras: string[] }
interface MapArea { id: string; name: string; exit_cameras: string[]; boxes: MapBox[] }
interface Mapping { cameras: MapCamera[]; areas: MapArea[]; summary: { cameras: number; assigned: number; online: number } }

const ONLINE = new Set(['online', 'replay']);
const valueOf = (c: MapCamera) => (c.role === 'exit' ? `exit:${c.area}` : c.box || '');

/** Picture of one camera, loaded again every few seconds (the newest frame; last saved frame if offline). */
function Tile({ cam, tick, onOpen }: { cam: MapCamera; tick: number; onOpen: () => void }) {
  const online = ONLINE.has(cam.state);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [cam.id, online]);
  const src = online && !failed
    ? `${API_BASE}${cam.snapshot_url}?w=480&q=50&t=${tick}`
    : `${API_BASE}${cam.last_snapshot_url}?t=${Math.floor(tick / 20)}`;
  const [gone, setGone] = useState(false);
  useEffect(() => { setGone(false); }, [src]);
  return (
    <button type="button" onClick={onOpen} title="Open the live view"
      className="relative block w-full aspect-video bg-black overflow-hidden cursor-zoom-in">
      {gone ? (
        <span className="absolute inset-0 flex items-center justify-center text-[11px] text-neutral-500">
          No picture yet
        </span>
      ) : (
        <img src={src} alt={cam.id} loading="lazy" className="w-full h-full object-contain"
          onError={() => { if (online && !failed) setFailed(true); else setGone(true); }} />
      )}
      <span className={`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${online ? 'bg-emerald-500 text-white' : 'bg-rose-600 text-white'}`}>
        {online ? 'LIVE' : 'OFFLINE'}
      </span>
      {!online && !gone && (
        <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded bg-black/70 text-[10px] text-neutral-200">last saved picture</span>
      )}
    </button>
  );
}

export function HoldingMappingPage() {
  const [data, setData] = useState<Mapping | null>(() => readLast<Mapping>('holding-mapping'));   // opens on the last known states
  const [error, setError] = useState<string | null>(null);
  const outage = useApiOutage();     // true only after 2 minutes without an answer: a restart shows no message
  const [tick, setTick] = useState(() => Date.now());
  const [areaFilter, setAreaFilter] = useState<string>('all');
  const [show, setShow] = useState<'all' | 'online' | 'unassigned'>('all');
  const [q, setQ] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [note, setNote] = useState<{ text: string; ok: boolean } | null>(null);
  const [open, setOpen] = useState<MapCamera | null>(null);
  // Multiview: 0 = the list with box selectors; N = an N x N wall of live video, paged
  const [multi, setMulti] = useState<number>(() => { try { return Number(localStorage.getItem('holding_multiview') || 0) || 0; } catch { return 0; } });
  const [menu, setMenu] = useState(false);
  const [page, setPage] = useState(0);
  useEffect(() => { try { localStorage.setItem('holding_multiview', String(multi)); } catch { /* not remembered */ } }, [multi]);
  // counting area per camera (outline on the picture): people are counted only inside it
  const [zones, setZones] = useState<Record<string, [number, number][]>>({});
  const [drawing, setDrawing] = useState(false);
  const loadZones = useCallback(() => baseapi.get('config').then(r => {
    const z: Record<string, [number, number][]> = {};
    for (const c of r.data?.cameras || []) if (Array.isArray(c.count_zone) && c.count_zone.length >= 3) z[c.id] = c.count_zone;
    setZones(z);
  }).catch(() => { /* keep what we have */ }), []);
  useEffect(() => { loadZones(); }, [loadZones]);
  useEffect(() => { setDrawing(false); }, [open?.id]);
  const noteTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const load = useCallback(() => baseapi.get('holding/mapping')
    .then(r => { setData(r.data); setError(null); if (r.data?.cameras) saveLast('holding-mapping', r.data); })
    .catch(() => setError('The mapping could not be loaded. The backend may need a restart to get this page’s API.')), []);

  useEffect(() => {
    load();
    const a = setInterval(load, 10000);                 // states and who-is-where
    const b = setInterval(() => setTick(Date.now()), 4000);   // pictures
    return () => { clearInterval(a); clearInterval(b); };
  }, [load]);

  const boxLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of data?.areas || []) {
      m.set(`exit:${a.id}`, `${a.id} exit`);
      for (const b of a.boxes) m.set(b.id, `${a.id} · Box ${b.number}`);
    }
    return m;
  }, [data]);
  const cameraOnBox = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of data?.areas || []) for (const b of a.boxes) if (b.cameras[0]) m.set(b.id, b.cameras[0]);
    return m;
  }, [data]);

  const say = (text: string, ok = true) => {
    setNote({ text, ok });
    if (noteTimer.current) clearTimeout(noteTimer.current);
    noteTimer.current = setTimeout(() => setNote(null), 6000);
  };

  const assign = async (cam: MapCamera, target: string) => {
    if (target === valueOf(cam)) return;
    setSaving(cam.id);
    try {
      const r = await baseapi.post('holding/mapping', { camera: cam.id, box: target || null, swap: true });
      setData(r.data);
      const moved = r.data.moved as { cameras: string[]; to: string | null } | null;
      const where = target ? boxLabel.get(target) || target : 'not assigned';
      say(`${cam.id} → ${where}` + (moved
        ? moved.to ? `.  ${moved.cameras.join(', ')} moved to ${boxLabel.get(moved.to) || moved.to} (swapped).`
          : `.  ${moved.cameras.join(', ')} is now not assigned.`
        : '.  Saved.'));
    } catch {
      say(`${cam.id} was not saved. Check the connection and try again.`, false);
    } finally {
      setSaving(null);
    }
  };

  const cams = useMemo(() => (data?.cameras || []).filter(c => {
    if (areaFilter !== 'all' && c.area !== areaFilter && !(areaFilter === 'none' && !c.area)) return false;
    if (show === 'online' && !ONLINE.has(c.state)) return false;
    if (show === 'unassigned' && c.role) return false;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const label = (boxLabel.get(valueOf(c)) || '').toLowerCase();
      if (![c.id, c.ip || '', c.name || '', label].some(x => x.toLowerCase().includes(s))) return false;
    }
    return true;
  }), [data, areaFilter, show, q, boxLabel]);

  const chip = (on: boolean) => `px-2.5 py-1 rounded-lg text-[11px] font-semibold border cursor-pointer transition ${on
    ? 'bg-blue-500 text-white border-blue-500'
    : 'bg-white dark:bg-[#181b23] text-neutral-600 dark:text-neutral-300 border-neutral-200 dark:border-[#262b37] hover:border-blue-400'}`;

  return (
    <div className="p-3 sm:p-4 space-y-3 text-neutral-900 dark:text-neutral-100">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/holding-area" className="inline-flex items-center gap-1 text-[12px] text-blue-600 dark:text-blue-400 hover:underline">
          <ArrowLeft className="w-3.5 h-3.5" /> Holding Area
        </Link>
        <h1 className="text-base sm:text-lg font-bold flex items-center gap-2">
          <Camera className="w-4 h-4 text-blue-500" /> Holding cameras · which camera is which box
        </h1>
        {data && (
          <span className="text-[11px] text-neutral-500 dark:text-neutral-400">
            {data.summary.cameras} cameras · {data.summary.online} live · {data.summary.assigned} assigned
          </span>
        )}
        {/* Multiview: the cameras as an N x N wall of live video, one screenful at a time */}
        <div className="ml-auto relative">
          <button onClick={() => setMenu(v => !v)} aria-haspopup="menu" aria-expanded={menu}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] font-semibold cursor-pointer ${multi ? 'bg-blue-500 text-white border-blue-500' : 'border-neutral-200 dark:border-[#262b37] hover:border-blue-400'}`}>
            <Grid3x3 className="w-3 h-3" /> {multi ? `Multiview ${multi}×${multi}` : 'Multiview'}
          </button>
          {menu && (
            <div role="menu" className="absolute right-0 mt-1 z-30 w-40 py-1 rounded-lg border border-neutral-200 dark:border-[#262b37] bg-white dark:bg-[#12151c] shadow-lg">
              {[1, 2, 3, 4, 5].map(k => (
                <button key={k} role="menuitem" onClick={() => { setMulti(k); setPage(0); setMenu(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-[12px] text-left cursor-pointer hover:bg-blue-500/10 ${multi === k ? 'text-blue-500 font-bold' : ''}`}>
                  {k === 1 ? <Square className="w-3.5 h-3.5" /> : <Grid3x3 className="w-3.5 h-3.5" />} {k}×{k} View
                </button>
              ))}
              <button role="menuitem" onClick={() => { setMulti(0); setMenu(false); }}
                className={`w-full px-3 py-1.5 text-[12px] text-left cursor-pointer border-t border-neutral-200 dark:border-[#262b37] hover:bg-blue-500/10 ${!multi ? 'text-blue-500 font-bold' : ''}`}>
                List with box selectors
              </button>
            </div>
          )}
        </div>
        <button onClick={() => { load(); setTick(Date.now()); }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-neutral-200 dark:border-[#262b37] text-[11px] font-semibold hover:border-blue-400 cursor-pointer">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <p className="text-[12px] text-neutral-600 dark:text-neutral-400 max-w-4xl">
        Look at each picture and choose the box it shows. The change is saved immediately. If the box you choose
        already has a camera, the two cameras swap boxes, so nothing is lost.
      </p>

      <div className="flex flex-wrap items-center gap-1.5">
        <button className={chip(areaFilter === 'all')} onClick={() => setAreaFilter('all')}>All areas</button>
        {(data?.areas || []).map(a => (
          <button key={a.id} className={chip(areaFilter === a.id)} onClick={() => setAreaFilter(a.id)}>{a.id}</button>
        ))}
        <span className="w-px h-5 bg-neutral-200 dark:bg-[#262b37] mx-1" />
        <button className={chip(show === 'all')} onClick={() => setShow('all')}>All cameras</button>
        <button className={chip(show === 'online')} onClick={() => setShow('online')}>Live only</button>
        <button className={chip(show === 'unassigned')} onClick={() => setShow('unassigned')}>Not assigned</button>
        <label className="ml-auto relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Camera, IP or box…"
            className="pl-7 pr-2 py-1 w-48 rounded-lg border border-neutral-200 dark:border-[#262b37] bg-white dark:bg-[#181b23] text-[12px] outline-none focus:border-blue-400" />
        </label>
      </div>

      {error && outage && <div className="p-3 rounded-lg border border-rose-300 bg-rose-50 dark:bg-[#2c1217] dark:border-rose-900/50 text-[12px] text-rose-700 dark:text-rose-300">{error}</div>}
      {!data && !(error && outage) && <div className="text-[12px] text-neutral-500">Loading cameras…</div>}
      {data && cams.length === 0 && <div className="text-[12px] text-neutral-500">No camera matches this filter.</div>}

      {multi > 0 && data && cams.length > 0 && (() => {
        const per = multi * multi; const pages = Math.max(1, Math.ceil(cams.length / per)); const pg = Math.min(page, pages - 1);
        const view = cams.slice(pg * per, pg * per + per);
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[11.5px] text-neutral-500 dark:text-neutral-400">
              <span>Cameras {pg * per + 1}–{pg * per + view.length} of {cams.length}</span>
              <button disabled={pg === 0} onClick={() => setPage(pg - 1)} aria-label="Previous cameras"
                className="ml-auto p-1 rounded-lg border border-neutral-200 dark:border-[#262b37] disabled:opacity-40 cursor-pointer hover:border-blue-400"><ChevronLeft className="w-3.5 h-3.5" /></button>
              <span>Page {pg + 1} / {pages}</span>
              <button disabled={pg >= pages - 1} onClick={() => setPage(pg + 1)} aria-label="Next cameras"
                className="p-1 rounded-lg border border-neutral-200 dark:border-[#262b37] disabled:opacity-40 cursor-pointer hover:border-blue-400"><ChevronRight className="w-3.5 h-3.5" /></button>
            </div>
            <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${multi}, minmax(0, 1fr))` }}>
              {view.map(cam => {
                const live = ONLINE.has(cam.state);
                return (
                  <button key={cam.id} type="button" onClick={() => setOpen(cam)} title="Open the large view"
                    className="relative aspect-video rounded-lg overflow-hidden bg-black border border-neutral-800 cursor-zoom-in text-left">
                    {live
                      ? <SavedFrame src={`${API_BASE}${cam.snapshot_url}`} alt={cam.id} size={multi > 1 ? 'tile' : 'full'} className="w-full h-full object-contain" />
                      : <img src={`${API_BASE}${cam.last_snapshot_url}`} alt={cam.id} className="w-full h-full object-contain opacity-60" />}
                    <span className="absolute top-0 inset-x-0 px-2 py-1 flex items-center gap-1.5 bg-gradient-to-b from-black/80 to-transparent text-white">
                      <span className={`w-1.5 h-1.5 rounded-full ${live ? 'bg-emerald-400' : 'bg-rose-500'}`} />
                      <span className="text-[11px] font-bold truncate">{cam.role ? boxLabel.get(valueOf(cam)) || valueOf(cam) : 'Not assigned'}</span>
                      <span className="text-[10px] font-mono text-white/70 truncate">{cam.id}</span>
                      {!live && <span className="text-[9.5px] px-1 rounded bg-rose-600">OFFLINE</span>}
                      {cam.people_in_view != null && <span className="ml-auto text-[10.5px] font-semibold">{cam.people_in_view} {zones[cam.id] ? 'in area' : 'in view'}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div className={`${multi > 0 ? 'hidden' : 'grid'} gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`}>
        {(multi > 0 ? [] : cams).map(cam => {
          const cur = valueOf(cam);
          return (
            <div key={cam.id} className="rounded-xl overflow-hidden border border-neutral-200 dark:border-[#262b37] bg-white dark:bg-[#181b23] shadow-xs">
              <Tile cam={cam} tick={tick} onOpen={() => setOpen(cam)} />
              <div className="p-2.5 space-y-2">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-[12px] font-bold">{cam.id}</span>
                  <span className="font-mono text-[10.5px] text-neutral-500">{cam.ip}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`shrink-0 px-2 py-1 rounded-md text-[12px] font-extrabold ${cam.role ? 'bg-blue-500/10 text-blue-600 dark:text-blue-300 border border-blue-500/30' : 'bg-amber-500/10 text-amber-600 dark:text-amber-300 border border-amber-500/30'}`}>
                    {cam.role ? boxLabel.get(cur) || cur : 'Not assigned'}
                  </span>
                  {cam.people_in_view != null && <span className="text-[10.5px] text-neutral-500">{cam.people_in_view} {zones[cam.id] ? 'in area' : 'in view'}</span>}
                  <span className={`ml-auto text-[10px] font-semibold ${zones[cam.id] ? 'text-cyan-500' : 'text-neutral-400'}`}>{zones[cam.id] ? 'area set' : 'no area'}</span>
                </div>
                <select value={cur} disabled={saving === cam.id} onChange={e => assign(cam, e.target.value)}
                  aria-label={`Box for ${cam.id}`}
                  className="w-full px-2 py-1.5 rounded-lg border border-neutral-200 dark:border-[#262b37] bg-white dark:bg-[#12151c] text-[12px] outline-none focus:border-blue-400 disabled:opacity-60">
                  <option value="">— Not assigned —</option>
                  {(data?.areas || []).map(a => (
                    <optgroup key={a.id} label={a.name || a.id}>
                      <option value={`exit:${a.id}`}>{a.id} · Exit camera</option>
                      {a.boxes.map(b => {
                        const other = cameraOnBox.get(b.id);
                        return (
                          <option key={b.id} value={b.id}>
                            Box {b.number} ({b.capacity} seats){other && other !== cam.id ? ` — now ${other}, will swap` : ''}
                          </option>
                        );
                      })}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
      </div>

      {note && (
        <div role="status" className={`fixed bottom-5 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl shadow-lg text-[12.5px] font-semibold flex items-center gap-2 ${note.ok ? 'bg-emerald-600 text-white' : 'bg-rose-600 text-white'}`}>
          {note.ok && <CheckCircle2 className="w-4 h-4" />} {note.text}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="w-[960px] max-w-full bg-[#0f1218] border border-[#262b37] rounded-xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-3 py-2 border-b border-[#262b37] text-neutral-100">
              <span className="font-mono text-[12px] font-bold">{open.id}</span>
              <span className="font-mono text-[11px] text-neutral-400">{open.ip}</span>
              <span className="text-[12px] text-blue-300">{open.role ? boxLabel.get(valueOf(open)) : 'Not assigned'}</span>
              <button onClick={() => setOpen(null)} className="ml-auto p-1 rounded hover:bg-white/10 cursor-pointer" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            {drawing ? (
              <ZoneEditor cameraId={open.id} initial={zones[open.id] || []}
                onSaved={pts => setZones(z => { const n = { ...z }; if (pts.length) n[open.id] = pts; else delete n[open.id]; return n; })}
                onClose={() => setDrawing(false)} />
            ) : (
            <div className="aspect-video bg-black relative">
              {!ONLINE.has(open.state)
                ? <img src={`${API_BASE}${open.last_snapshot_url}`} alt={open.id} className="w-full h-full object-contain" />
                : <LiveFrame src={`${API_BASE}${open.snapshot_url}`} alt={open.id} className="w-full h-full object-contain" />}
              {ONLINE.has(open.state) && (
                <button onClick={() => setDrawing(true)}
                  className="absolute bottom-2 left-2 px-2.5 py-1 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-[11px] font-semibold cursor-pointer">
                  {zones[open.id] ? 'Edit counting area' : 'Draw counting area'}
                </button>
              )}
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
